import {
  adminNavLinks,
  carteiraNavLink,
  chatNavLink,
  dashboardNavLink,
  objectiveNavSections,
  publicNavLinks,
  toolsNavLink,
} from '@/config/navigation-links-config';
import { carteiraPath } from '@/libs/carteira-route';
import { ativoPath } from '@/libs/ativo-route';
import { COURSE_FEATURE_FLAG, coursesPath } from '@/libs/course/course-routes';

export type SiteScreen = {
  id: string;
  title: string;
  description: string;
  route: string;
  requiresAuth?: boolean;
  adminOnly?: boolean;
  /** Screen only exists while this feature flag is enabled for the user. */
  requiresFeatureFlag?: string;
};

const publicScreens: SiteScreen[] = [
  ...publicNavLinks.map((link) => ({
    id: link.url.slice(1),
    title: link.title,
    description: `Acesse ${link.title.toLowerCase()}.`,
    route: link.url,
  })),
  ...objectiveNavSections.flatMap((section) =>
    section.links.map((link) => ({
      id:
        link.url === '/ideias-para-investir-por-objetivo'
          ? 'ideias-por-objetivo'
          : link.url.slice(1).replaceAll('/', '-'),
      title: link.title,
      description: link.description ?? `Acesse ${link.title.toLowerCase()}.`,
      route: link.url,
      requiresAuth: link.requiresAuth,
    })),
  ),
  {
    id: 'sobre',
    title: 'Sobre',
    description: 'Conheça o Análise de FIIs Pro.',
    route: '/sobre',
  },
];

const uniqueScreens = (screens: SiteScreen[]) => {
  const seen = new Set<string>();
  return [...screens]
    .reverse()
    .filter((screen) => {
      if (seen.has(screen.id)) return false;
      seen.add(screen.id);
      return true;
    })
    .reverse();
};

export const SITE_SCREENS: SiteScreen[] = uniqueScreens([
  ...publicScreens,
  {
    id: 'painel',
    title: dashboardNavLink.title,
    description: dashboardNavLink.description ?? 'Seu painel pessoal.',
    route: dashboardNavLink.url,
    requiresAuth: true,
  },
  {
    id: 'carteira',
    title: carteiraNavLink.title,
    description: carteiraNavLink.description ?? 'Sua carteira de investimentos.',
    route: carteiraPath.index(),
    requiresAuth: true,
  },
  {
    id: 'chat',
    title: chatNavLink.title,
    description: chatNavLink.description ?? 'Converse com o assistente.',
    route: chatNavLink.url,
    requiresAuth: true,
  },
  {
    id: 'ferramentas',
    title: toolsNavLink.title,
    description: toolsNavLink.description ?? 'Escolha uma ferramenta.',
    route: toolsNavLink.url,
  },
  {
    id: 'relatorios-ai',
    title: 'Relatórios IA',
    description: 'Crie e agende briefings personalizados por e-mail.',
    route: '/ferramentas/relatorios-ai',
    requiresAuth: true,
  },
  {
    id: 'fii-resumo',
    title: 'Resumo do FII',
    description: 'Resumo e indicadores de um fundo imobiliário.',
    route: `${ativoPath.index()}/[ticker]/resumo`,
  },
  {
    id: 'cursos',
    title: 'Cursos',
    description: 'Catálogo de cursos da plataforma.',
    route: coursesPath(),
    requiresFeatureFlag: COURSE_FEATURE_FLAG,
  },
  {
    id: 'curso-aula',
    title: 'Aula do curso',
    description: 'Abre uma aula específica com os parâmetros curso, modulo e aula.',
    route: `${coursesPath()}/[curso]/[modulo]/[aula]`,
    requiresFeatureFlag: COURSE_FEATURE_FLAG,
  },
  ...adminNavLinks.map((link) => ({
    id: `admin-${link.url.slice(7).replaceAll('/', '-')}`,
    title: link.title,
    description: `Administre ${link.title.toLowerCase()}.`,
    route: link.url,
    adminOnly: true,
  })),
]);

export function resolveScreenRoute(screenId: string, params?: Record<string, string>): string | null {
  const screen = SITE_SCREENS.find((item) => item.id === screenId);
  if (!screen) return null;
  return screen.route
    .replace(/\[([^\]]+)\]/g, (_, key: string) => {
      const value = params?.[key];
      return value === undefined ? `[${key}]` : encodeURIComponent(value);
    })
    .includes('[')
    ? null
    : screen.route.replace(/\[([^\]]+)\]/g, (_, key: string) => encodeURIComponent(params?.[key] ?? ''));
}

export function listScreensFor(ctx: {
  isAdmin: boolean;
  featureFlags?: Readonly<Record<string, boolean>>;
}): SiteScreen[] {
  return SITE_SCREENS.filter(
    (screen) =>
      (!screen.adminOnly || ctx.isAdmin) &&
      (!screen.requiresFeatureFlag || ctx.featureFlags?.[screen.requiresFeatureFlag] === true),
  );
}
